import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, firstValueFrom, from, map, Observable, of, retry, shareReplay, switchMap, tap, throwError, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserAuthService } from './auth/userAuth.service';
import { Router } from '@angular/router';
import { HospitalStateService } from './services/hospitalstate.service';
import { IpdFeatureService } from './services/ipd-feature.service';
import { EncryptionService } from './services/encryption.service';
import { inject } from '@angular/core';
import { CONSULTATION_ROLES } from './common/constant/constants.type';
import { UserRole } from './common/authorization/roles';
import { CsrfTokenService } from './auth/csrf-token.service';
import { LoginResult } from './models/common.response';

@Injectable({ providedIn: 'root' })
export class LoginService {
  private readonly CHECK_INTERVAL_MS = 30_000;
  private _authenticated: boolean = false;
  private _userResult: LoginResult | null = null;
  private _lastSessionCheckAt = 0;
  private _sessionValidation$: Observable<boolean> | null = null;

  private _httpclient = inject(HttpClient);
  private _userService = inject(UserAuthService);
  private _router = inject(Router);
  private _hospital = inject(HospitalStateService);
  private _ipdFeature = inject(IpdFeatureService);
  private _encryption = inject(EncryptionService);
  private _csrfToken = inject(CsrfTokenService);

  constructor() { }

  // Access token is now handled by HttpOnly cookies.
  // JS no longer has access to it.
  get accessToken(): string {
    return "";
  }

  private getDecodedToken(): any | null {
    return this._userResult;
  }

  private applyAuthenticatedUser(userResult: any): void {
    this._userResult = userResult;
    this._authenticated = true;
    this._lastSessionCheckAt = Date.now();
    this._userService.user = userResult.userName;
  }

  private loadHospital(hospitalId: number | null): Observable<any> {
    if (!hospitalId) {
      this._hospital.clearHospital();
      return of(null);
    }

    return this._userService.getHospital(hospitalId).pipe(
      tap(res => {
        this._hospital.setHospital(res.result);
      }),
      catchError(() => {
        this._hospital.clearHospital();
        return of(null);
      })
    );
  }

  signIn(credentials: { username: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      this._router.navigate(['/dashboard']);
      // return throwError('User is already logged in.');
    }

    return this._encryptAndLogin(credentials).pipe(
      catchError(() => {
        // If decryption failed on server (e.g. key rotated), refetch key and retry once
        this._encryption.clearPublicKey();
        return this._encryptAndLogin(credentials);
      })
    );
  }

  private _encryptAndLogin(credentials: { username: string; password: string }): Observable<any> {
    return from(this._encryption.fetchServerKeyAndTime()).pipe(
      switchMap(() => from(this._encryption.encryptPassword(credentials.password))),
      switchMap((encryptedPassword) => {
        const payload = { userName: credentials.username, password: encryptedPassword };
        return this._httpclient.post(`${environment.baseURl}/Auth/login`, payload, { withCredentials: true });
      }),
      switchMap((response: any) => {
        this._csrfToken.clearToken();
        this.applyAuthenticatedUser(response.result);

        const hospitalId = this.getHospitalId() as number;
        return this.loadHospital(hospitalId).pipe(
          map(() => response)
        );
      })
    );
  }

  clearSession(): void {
    this._csrfToken.clearToken();
    this._userResult = null;
    this._authenticated = false;
    this._lastSessionCheckAt = 0;
    this._sessionValidation$ = null;
  }

  isIPDTabShown(): boolean {
    return this._ipdFeature.isEnabled;
  }

  isAbhaEnabled(): boolean {
    return !!this._userResult?.isAbhaEnabled;
  }

  isAbdmHipEnabled(): boolean {
    return !!this._userResult?.isAbdmHipEnabled;
  }

  isAbdmHiuEnabled(): boolean {
    return !!this._userResult?.isAbdmHiuEnabled;
  }

  initializeAuth() {
    // On anonymous auth pages (login, forgot/reset password) there is no session
    // yet. Skipping the /Auth/me probe here avoids the 401 that would otherwise
    // trip the interceptor's logout and bounce the user off a reset-password link.
    if (window.location.pathname.includes('/authentication')) {
      return Promise.resolve(null);
    }

    return firstValueFrom(
      this._httpclient.get(`${environment.baseURl}/Auth/me`, { withCredentials: true }).pipe(
        retry({
          count: 2,
          delay: (error: unknown) => {
            if (error instanceof HttpErrorResponse && error.status === 429) {
              return timer(2000);
            }

            return throwError(() => error);
          }
        }),
        tap((response: any) => {
          this.applyAuthenticatedUser(response.result);
        }),
        switchMap((response: any) => {
          const hospitalId = response.result.hospitalId;
          return this.loadHospital(hospitalId);
        }),
        catchError((error: unknown) => {
          // Only an explicit rejection means the session is gone. A 429 (rate limit)
          // or network blip on this one probe must not log the user out on refresh.
          if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            this.clearSession();
          }

          return of(null);
        })
      )
    );
  }

  validateActiveSession(): Observable<boolean> {
    const activeUserId = this.getUserId();

    if (!activeUserId) {
      return of(false);
    }

    if (this._lastSessionCheckAt && Date.now() - this._lastSessionCheckAt < this.CHECK_INTERVAL_MS) {
      return of(true);
    }

    if (this._sessionValidation$) {
      return this._sessionValidation$;
    }

    this._sessionValidation$ = this._httpclient.get(`${environment.baseURl}/Auth/me`, { withCredentials: true }).pipe(
      switchMap((response: any) => {
        const serverUser = response?.result;
        const serverUserId = serverUser?.id ? Number(serverUser.id) : null;

        if (!serverUserId || serverUserId !== activeUserId) {
          this.clearSession();
          return of(false);
        }

        this.applyAuthenticatedUser(serverUser);
        return this.loadHospital(serverUser.hospitalId ? Number(serverUser.hospitalId) : null).pipe(
          map(() => true)
        );
      }),
      catchError(() => {
        this.clearSession();
        return of(false);
      }),
      finalize(() => {
        this._sessionValidation$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this._sessionValidation$;
  }

  getHospitalFromToken(): string[] | null {
    if (!this._userResult) return null;
    return this._userResult.hospitalId ? [this._userResult.hospitalId.toString()] : [];
  }

  getHospitalId(): number | null {
    return this._userResult?.hospitalId ? Number(this._userResult.hospitalId) : null;
  }

  getUserRoleFromToken(): UserRole[] | null {
    return this._userResult?.roles as UserRole[] | undefined ?? null;
  }

  hasAnyRole(allowedRoles: readonly UserRole[]): boolean {
    const roles = this.getUserRoleFromToken() || [];
    return allowedRoles.some(role => roles.includes(role));
  }

  isDoctor(): boolean {
    const roles = this.getUserRoleFromToken();
    return roles?.includes(UserRole.Doctor) ?? false;
  }

  canStartConsultation(): boolean {
    return this.hasAnyRole(CONSULTATION_ROLES);
  }

  getUserId(): number | null {
    return this._userResult?.id ? Number(this._userResult.id) : null;
  }
}
